<template>
  <div>
    <pack-info :packId="packId"></pack-info>
    <v-container fluid>
      <v-row justify-center class="och-topMargin--packInfoOffset" no-gutters>
        <v-col sm="12">
          <phi-warning class="mb-2"></phi-warning>
        </v-col>
        <v-col sm="12">
          <v-toolbar dense dark color="primary white--text elevation-0">
            <v-toolbar-title class="font-weight-bold">
              {{ releaseNote.title }}
              <span class="font-weight-thin">(ID: {{ releaseNoteId }})</span>
            </v-toolbar-title>
            <v-spacer></v-spacer>
            <filtered-navigation-mini
              :filters="filterListWithOverrides"
              :navigation="navigation"
              class="mr-3"
            ></filtered-navigation-mini>

            <v-tooltip v-if="isUatOpen" left>
              <template v-slot:activator="{ on }">
                <span v-on="on" class="d-inline-block">
                  <v-btn
                    icon
                    @click="edit"
                    :disabled="!hasTestPlanEdit"
                    data-cy="testPlan__button--edit"
                    data-test="test-plan-edit-button"
                  >
                    <v-icon small>fas fa-edit</v-icon>
                  </v-btn>
                </span>
              </template>
              <span data-test="has-permission-tooltip" v-if="hasTestPlanEdit"
                >Update this release note's testing status.</span
              >
              <span data-test="no-permission-tooltip" v-else
                >You don't have permission to edit the testing status.</span
              >
            </v-tooltip>
            <div v-if="!isUatOpen">
              <v-tooltip left>
                <template v-slot:activator="{ on }">
                  <v-icon color="grey lighten-2" data-cy="testPlan__icon--lock" v-on="on">fas fa-lock</v-icon>
                </template>
                <span>UAT is currently closed; editing has been disabled.</span>
              </v-tooltip>
            </div>
          </v-toolbar>
        </v-col>
      </v-row>
      <v-row justify-center no-gutters>
        <v-col sm="12" class="och-border--all">
          <v-tabs v-model="activeTab" background-color="grey lighten-3">
            <v-tab>Release Note</v-tab>
            <v-tab data-cy="testPlanView__tab--testPlan" v-if="showTestPlanTab">Test Plan</v-tab>
            <v-tab data-cy="testPlanView__tab--changeHistory">Member Change History</v-tab>
            <v-tab-item>
              <test-plan-status :releaseNoteId="releaseNoteId"></test-plan-status>
              <release-note :packId="packId" :releaseNoteId="releaseNoteId"></release-note>
              <filtered-navigation :filters="filterListWithOverrides" :navigation="navigation"></filtered-navigation>
            </v-tab-item>
            <v-tab-item v-if="showTestPlanTab">
              <test-plan-status :releaseNoteId="releaseNoteId"></test-plan-status>
              <v-card class="och-testPlanCard__border--bottom elevation-0">
                <v-row>
                  <v-col sm="6">
                    <v-card class="och-testPlanCard__border--right elevation-0" v-if="releaseNote.requiredSetup">
                      <v-card-title class="pb-0">
                        <v-row>
                          <v-col sm="10" class="pt-2 font-weight-black primary--text subtitle-1">UAT PURPOSE</v-col>
                        </v-row>
                      </v-card-title>
                      <v-card-text>
                        <embedded-html
                          :value="releaseNote.requiredSetup"
                          class="mt-2"
                          data-cy="testPlanTab-uatPurpose"
                        ></embedded-html>
                      </v-card-text>
                    </v-card>
                  </v-col>
                  <v-col sm="6">
                    <v-card class="elevation-0">
                      <v-card-title class="pb-0">
                        <v-row row>
                          <v-col sm="10" class="pt-2 font-weight-black primary--text subtitle-1"
                            >UAT ENVIRONMENTS</v-col
                          >
                        </v-row>
                      </v-card-title>
                      <v-card-text>
                        <div class="mt-2" v-html="releaseNote.uatEnvironment || `<i>(No value)</i>`"></div>
                      </v-card-text>
                    </v-card>
                  </v-col>
                </v-row>
              </v-card>
              <v-card>
                <v-card-title class="pb-0">
                  <v-row row>
                    <v-col sm="6" class="pt-3 font-weight-black primary--text subtitle-1"
                      >TEST PLAN SET-UP ({{ testSetupList.length }})</v-col
                    >
                  </v-row>
                </v-card-title>
                <v-card-text class="pt-0" style="border-bottom: 1px solid #ccc">
                  <v-data-table
                    :headers="headers"
                    :items="testSetupList"
                    :hide-default-footer="true"
                    disable-pagination
                    no-data-text="There are currently no test set-ups for this release note"
                  >
                    <template v-slot:item="{ index, item }">
                      <tr :key="index">
                        <td>{{ item.setupIdentifier || "--" }}</td>
                        <td>
                          <span v-html="item.setup || '--'"></span>
                        </td>
                      </tr>
                    </template>
                  </v-data-table>
                </v-card-text>
              </v-card>
              <v-card>
                <v-card-title class="pb-0 mb-0">
                  <v-row>
                    <v-col sm="6" class="pt-3 font-weight-black primary--text subtitle-1"
                      >TEST STEPS ({{ memberTestStepListLength }})</v-col
                    >
                  </v-row>
                </v-card-title>
                <v-card-text>
                  <member-test-step-table
                    :releaseNoteId="releaseNoteId"
                    :serviceAreaId="serviceAreaId"
                  ></member-test-step-table>
                </v-card-text>
              </v-card>
            </v-tab-item>
            <v-tab-item>
              <test-plan-audit-log :releaseNoteId="releaseNoteId"></test-plan-audit-log>
            </v-tab-item>
          </v-tabs>
        </v-col>
      </v-row>
    </v-container>
  </div>
</template>

<script>
import EmbeddedHtml from "@/components/embeddedHtml";
import FilteredNavigation from "@/components/filteredNavigation";
import FilteredNavigationMini from "@/components/filteredNavigationMini";
import PackInfo from "@/components/packInfo";
import PhiWarning from "@/components/PhiWarning";
import ReleaseNote from "@/components/releaseNote";
import TestPlanStatus from "@/components/testPlanStatus";
import MemberTestStepTable from "@/components/memberTestStepTable";
import TestPlanAuditLog from "./TestPlanAuditLog.vue";
import { mapActions, mapGetters, mapState } from "vuex";
import { get } from "lodash";

export default {
  name: "testPlanView",
  components: {
    EmbeddedHtml,
    FilteredNavigation,
    FilteredNavigationMini,
    MemberTestStepTable,
    PackInfo,
    PhiWarning,
    ReleaseNote,
    TestPlanStatus,
    TestPlanAuditLog,
  },
  computed: {
    activeTab: {
      get() {
        const { tab } = this.$route.query;
        const index = this.tabs.indexOf(tab);
        return index < 0 ? 0 : index;
      },
      set(index) {
        const tab = this.tabs[index];
        this.$router.push({ query: { tab } });
      },
    },
    filters() {
      return (
        get(
          this,
          `$store.state.session.user.userConfiguration.widgets.releaseNoteList.filters[${this.searchContext}]`
        ) || []
      );
    },
    filterListWithOverrides() {
      const concatenatedFilters = this.filters.concat(this.userConfig.filterOverrideList || []);
      return concatenatedFilters;
    },
    filterOverrideListIsUndefined() {
      return this.userConfig.filterOverrideList === undefined;
    },
    searchContext() {
      if (this.isGlobalSearch) {
        return "global";
      }
      return this.packId;
    },
    sortBy() {
      return this.$store.state.session.user.userConfiguration.widgets.releaseNoteList.sortBy || [];
    },
    sortDesc() {
      return this.$store.state.session.user.userConfiguration.widgets.releaseNoteList.sortDesc || [];
    },
    isGlobalSearch() {
      return !this.packId || !!this.$route.query.globalSearch;
    },
    packId() {
      return this.$route.params.packId;
    },
    hasTestPlanData() {
      const uatEnvironment = this.$store.state.releaseNote.releaseNoteRecord.uatEnvironment;
      const uatPurpose = this.$store.state.releaseNote.releaseNoteRecord.requiredSetup;
      const testPlanSetup = this.$store.state.releaseNote.releaseNoteRecord.testPlanSetup;
      const testSteps = this.$store.state.releaseNote.releaseNoteRecord.testSteps;
      if (uatEnvironment || uatPurpose || testPlanSetup || testSteps) {
        return true;
      }

      return false;
    },
    memberTestStepList() {
      return this.$store.getters["testStep/memberTestStepList"](this.releaseNoteId);
    },
    memberTestStepListLength() {
      // Filter out the substeps
      if (this.memberTestStepList) {
        const filteredList = this.memberTestStepList.filter(step => !step.substepNumber);
        return filteredList.length;
      }
    },
    packId() {
      return parseInt(this.$route.params.packId, 10);
    },
    releaseNote() {
      return this.$store.state.releaseNote.releaseNoteRecord;
    },
    releaseNoteId() {
      return parseInt(this.$route.params.releaseNoteId, 10);
    },
    serviceAreaId() {
      return this.$route.params.serviceAreaId;
    },
    showTestPlanTab() {
      let releaseNoteDatePublished;
      if (this.releaseNote.datePublished) {
        releaseNoteDatePublished = new Date(this.releaseNote.datePublished).getTime();
      }
      const dateConditionForShowingTabChanged = new Date("12/16/2024").getTime();

      if (releaseNoteDatePublished && releaseNoteDatePublished < dateConditionForShowingTabChanged) {
        // Before 12/16/2024, hide the test plan tab in all cases excepct when the operationalize type is "UAT Needed"
        return this.operationalizeType === "UAT Needed";
      }
      // On or after 12/16/2024, show the test plan tab in all cases except when the operationalize type is "FYI" or the template type is "rte" or "communicationOnly"
      if (this.templateType === "rte" || this.templateType === "communicationOnly") {
        return false;
      }
      if (this.operationalizeType !== "FYI") {
        return true;
      }
      return false;
    },
    testSetupList() {
      return this.$store.state.releaseNote.releaseNoteRecord.individualSetup || [];
    },
    theme() {
      return this.$store.state.session.theme;
    },
    userConfig() {
      return this.$store.state.session.user.userConfiguration;
    },
    ...mapGetters({
      isUatOpen: "pack/isUatOpen",
      featureFlagMap: "featureFlag/getFlagMap",
      hasTestPlanEdit: "session/hasTestPlanEdit",
    }),
    ...mapState({
      operationalizeType: state => state.releaseNote.releaseNoteRecord.operationalizeType,
      templateType: state => state.releaseNote.releaseNoteRecord.templateType,
      navigation: state => state.releaseNote.navigationOptionMap,
    }),
  },
  async created() {
    try {
      await Promise.all([
        this.$store.dispatch("releaseNote/getTestPlanList", this.packId),
        this.getReleaseNote(this.releaseNoteId),
        this.getNav({
          searchContext: this.searchContext,
          releaseNoteId: this.releaseNoteId,
          sortBy: this.sortBy,
          sortDesc: this.sortDesc,
        }),
      ]);
    } catch (error) {
      console.error(error);
      this.$notifyError("Unable to fetch test plan data.");
    }
  },
  data() {
    return {
      tabs: ["release-note", "test-steps", "change-history"],
      headers: [
        {
          text: "Identifier",
          align: "left",
          sortable: false,
          value: "setupIdentifier",
        },
        {
          text: "Setup",
          align: "left",
          sortable: false,
          value: "setup",
        },
      ],
      search: null,
    };
  },
  methods: {
    edit() {
      this.$router.push({
        name: "testPlanEdit",
        params: {
          packId: this.packId,
          releaseNoteId: this.releaseNoteId,
          serviceAreaId: this.serviceAreaId,
        },
      });
    },
    getReleaseNote(id) {
      return this.$store.dispatch("releaseNote/getReleaseNoteRecord", id);
    },
    ...mapActions({
      getNav: "releaseNote/getNav",
    }),
  },
  watch: {
    async releaseNoteId(val) {
      const { searchContext, releaseNoteId, sortBy, sortDesc } = this;
      await this.getNav({ searchContext, releaseNoteId, sortBy, sortDesc });
      await this.getReleaseNote(val);
    },
  },
};
</script>
